import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Badge,
  Tooltip,
  Alert
} from '@mui/material'
import {
  Comment,
  Add,
  ExpandMore,
  ExpandLess,
  Reply,
  CheckCircle,
  Schedule,
  Cancel,
  Edit,
  Delete,
  Send,
  Close
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import axios from 'axios'

const COMPONENT_NAMES = {
  informacion_general: 'Información General',
  definiciones: 'Definiciones',
  marco_normativo: 'Marco Normativo',
  actividades: 'Actividades',
  diagrama: 'Diagrama de Flujo'
}

const ESTADO_COLORS = {
  abierto: 'warning',
  resuelto: 'success',
  cerrado: 'default'
}

const ESTADO_ICONS = {
  abierto: <Schedule />,
  resuelto: <CheckCircle />,
  cerrado: <Cancel />
}

const ComentarioComponent = ({
  procedimientoId,
  componente,
  componenteItemId = null,
  title,
  onComentariosChange = () => {}
}) => {
  const { user, isEditor, isAdmin } = useAuth()
  const [comentarios, setComentarios] = useState([])
  const [tiposComentario, setTiposComentario] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false)
  const [openRespuestaDialog, setOpenRespuestaDialog] = useState(false)
  const [selectedComentario, setSelectedComentario] = useState(null)

  // Form states
  const [nuevoComentario, setNuevoComentario] = useState({
    comentario: '',
    tipo_comentario_id: ''
  })
  const [nuevaRespuesta, setNuevaRespuesta] = useState('')

  // Expansion state
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    loadComentarios()
    loadTiposComentario()
  }, [procedimientoId, componente])

  const loadComentarios = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const params = new URLSearchParams({
        componente: componente
      })

      const response = await axios.get(
        `/api/comentarios/procedimiento/${procedimientoId}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )

      setComentarios(response.data || [])
      onComentariosChange(response.data?.length || 0)
    } catch (error) {
      console.error('Error cargando comentarios:', error)
      setError('Error al cargar comentarios')
    }
  }

  const loadTiposComentario = async () => {
    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/comentarios/tipos', {
        headers: { Authorization: `Bearer ${token}` }
      })
      setTiposComentario(response.data || [])
    } catch (error) {
      console.error('Error cargando tipos de comentario:', error)
    }
  }

  const handleCreateComentario = async () => {
    if (!nuevoComentario.comentario.trim() || !nuevoComentario.tipo_comentario_id) {
      setError('Por favor complete todos los campos')
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.post('/api/comentarios', {
        procedimiento_id: procedimientoId,
        componente: componente,
        componente_item_id: componenteItemId,
        tipo_comentario_id: parseInt(nuevoComentario.tipo_comentario_id),
        comentario: nuevoComentario.comentario
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Comentario creado exitosamente')
      setOpenDialog(false)
      setNuevoComentario({ comentario: '', tipo_comentario_id: '' })
      loadComentarios()
    } catch (error) {
      setError('Error al crear comentario: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleResolverComentario = async (comentarioId, notasResolucion = '') => {
    setLoading(true)
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.put(`/api/comentarios/${comentarioId}/resolver`, {
        notas_resolucion: notasResolucion
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Comentario resuelto exitosamente')
      loadComentarios()
    } catch (error) {
      setError('Error al resolver comentario: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleCreateRespuesta = async () => {
    if (!nuevaRespuesta.trim()) {
      setError('Por favor escriba una respuesta')
      return
    }

    setLoading(true)
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.post('/api/comentarios/respuestas', {
        comentario_id: selectedComentario.id,
        respuesta: nuevaRespuesta
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Respuesta agregada exitosamente')
      setOpenRespuestaDialog(false)
      setNuevaRespuesta('')
      setSelectedComentario(null)
      loadComentarios()
    } catch (error) {
      setError('Error al crear respuesta: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteComentario = async (comentarioId) => {
    if (!window.confirm('¿Está seguro de eliminar este comentario?')) return

    setLoading(true)
    try {
      const token = localStorage.getItem('sgc_token')
      await axios.delete(`/api/comentarios/${comentarioId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      setSuccess('Comentario eliminado exitosamente')
      loadComentarios()
    } catch (error) {
      setError('Error al eliminar comentario: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const canEditComentario = (comentario) => {
    return comentario.usuario_id === user?.id || isAdmin || isEditor
  }

  const canResolveComentario = (comentario) => {
    return (isAdmin || isEditor) && comentario.estado === 'abierto'
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const comentariosCount = comentarios.length
  const comentariosAbiertos = comentarios.filter(c => c.estado === 'abierto').length

  return (
    <Box sx={{ mb: 2 }}>
      {/* Header del componente de comentarios */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            startIcon={<Comment />}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            onClick={() => setExpanded(!expanded)}
            variant="text"
            size="small"
          >
            Comentarios {title && `- ${title}`}
            {comentariosCount > 0 && (
              <Badge
                badgeContent={comentariosAbiertos}
                color="warning"
                sx={{ ml: 1 }}
              >
                <Chip
                  label={comentariosCount}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Badge>
            )}
          </Button>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          onClick={() => setOpenDialog(true)}
        >
          Agregar Comentario
        </Button>
      </Box>

      {/* Lista de comentarios */}
      <Collapse in={expanded}>
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            {comentarios.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                No hay comentarios para este componente
              </Typography>
            ) : (
              <List sx={{ p: 0 }}>
                {comentarios.map((comentario, index) => (
                  <React.Fragment key={comentario.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: comentario.tipo_comentario_color }}>
                          {comentario.usuario_nombre?.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {comentario.usuario_nombre}
                            </Typography>

                            <Chip
                              label={comentario.tipo_comentario_nombre}
                              size="small"
                              sx={{
                                bgcolor: comentario.tipo_comentario_color + '20',
                                color: comentario.tipo_comentario_color,
                                border: `1px solid ${comentario.tipo_comentario_color}50`
                              }}
                            />

                            <Chip
                              label={comentario.estado.toUpperCase()}
                              size="small"
                              color={ESTADO_COLORS[comentario.estado]}
                              icon={ESTADO_ICONS[comentario.estado]}
                            />

                            <Typography variant="caption" color="text.secondary">
                              {formatDate(comentario.created_at)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              {comentario.comentario}
                            </Typography>

                            {comentario.estado === 'resuelto' && comentario.notas_resolucion && (
                              <Box sx={{
                                p: 1,
                                bgcolor: 'success.50',
                                borderLeft: 3,
                                borderColor: 'success.main',
                                mb: 1
                              }}>
                                <Typography variant="caption" color="success.main" fontWeight="bold">
                                  Resolución por {comentario.resuelto_por_nombre}:
                                </Typography>
                                <Typography variant="body2">
                                  {comentario.notas_resolucion}
                                </Typography>
                              </Box>
                            )}

                            {/* Respuestas */}
                            {comentario.respuestas && comentario.respuestas.length > 0 && (
                              <Box sx={{ ml: 2, mt: 1, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
                                {comentario.respuestas.map((respuesta) => (
                                  <Box key={respuesta.id} sx={{ mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" fontWeight="bold">
                                        {respuesta.usuario_nombre}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {formatDate(respuesta.created_at)}
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2">
                                      {respuesta.respuesta}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}

                            {/* Acciones */}
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <Button
                                size="small"
                                startIcon={<Reply />}
                                onClick={() => {
                                  setSelectedComentario(comentario)
                                  setOpenRespuestaDialog(true)
                                }}
                              >
                                Responder
                              </Button>

                              {canResolveComentario(comentario) && (
                                <Button
                                  size="small"
                                  startIcon={<CheckCircle />}
                                  color="success"
                                  onClick={() => handleResolverComentario(comentario.id)}
                                >
                                  Resolver
                                </Button>
                              )}

                              {canEditComentario(comentario) && (
                                <Button
                                  size="small"
                                  startIcon={<Delete />}
                                  color="error"
                                  onClick={() => handleDeleteComentario(comentario.id)}
                                >
                                  Eliminar
                                </Button>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < comentarios.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Collapse>

      {/* Dialog para nuevo comentario */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Agregar Comentario - {COMPONENT_NAMES[componente]}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Comentario</InputLabel>
              <Select
                value={nuevoComentario.tipo_comentario_id}
                label="Tipo de Comentario"
                onChange={(e) => setNuevoComentario({
                  ...nuevoComentario,
                  tipo_comentario_id: e.target.value
                })}
              >
                {tiposComentario.map((tipo) => (
                  <MenuItem key={tipo.id} value={tipo.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: tipo.color
                        }}
                      />
                      {tipo.nombre}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Comentario"
              placeholder="Escriba su comentario, solicitud de cambio u observación..."
              value={nuevoComentario.comentario}
              onChange={(e) => setNuevoComentario({
                ...nuevoComentario,
                comentario: e.target.value
              })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleCreateComentario}
            variant="contained"
            disabled={loading}
            startIcon={<Send />}
          >
            Crear Comentario
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para respuesta */}
      <Dialog open={openRespuestaDialog} onClose={() => setOpenRespuestaDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Responder Comentario
          <IconButton
            onClick={() => setOpenRespuestaDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedComentario && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Comentario original de {selectedComentario.usuario_nombre}:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedComentario.comentario}
                </Typography>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Su Respuesta"
                placeholder="Escriba su respuesta..."
                value={nuevaRespuesta}
                onChange={(e) => setNuevaRespuesta(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRespuestaDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleCreateRespuesta}
            variant="contained"
            disabled={loading}
            startIcon={<Send />}
          >
            Enviar Respuesta
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ComentarioComponent